import { Observable } from 'rxjs';

import { TrackingDevice } from './tracking-device.model';
import { ImmutableMap, Prop, QueryParams, RequiredOnly, isObject, isString } from './utility.model';

export const MAX_USER_CALLEE_COUNT = 10;

export const USER_ROLES = ['owner', 'member', 'partner', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_LANGS = ['ja', 'en'] as const;
export type UserLang = (typeof USER_LANGS)[number];

export type UserSortItems = 'name' | 'serviceId';

export const USER_TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'Asia/Kolkata',
  'Asia/Yangon',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Australia/Perth',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;
export type UserTimezone = (typeof USER_TIMEZONES)[number];

type UserRequiredProps = {
  email: string;
  name: string;
  role: UserRole;
  lang: UserLang;
  tz: UserTimezone;
  groupId: string;
};

type UserOptionalProps = {
  serviceId?: string;
  supportTeam?: string;
  callees?: string[];
  phoneNumber?: string;
  countryCode?: string;
  memo?: string;
  avatar?: string;
  organizationId?: string;
};

type UserProvidedProps = {
  userId: string;
  enabled: boolean;
  organizer?: boolean;
  mfa?: boolean;
  updatedAt: number;
  version: number;
  units: string[];
  working: string;
  assignedAt: string;
};

export type UserQueryParams = QueryParams & { organizationId?: string };

export type UserCreateParams = UserRequiredProps & UserOptionalProps;

export type UserUpdateParams = Partial<Omit<UserRequiredProps, 'email'>> & UserOptionalProps;

export type User = UserRequiredProps &
  Omit<UserOptionalProps, 'organizationId'> &
  UserProvidedProps & { trackingDevices$: Observable<TrackingDevice[]> };

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

export function isUserCreateParams(value: unknown): value is UserCreateParams {
  const sProps: Prop<RequiredOnly<UserCreateParams>, string>[] = ['email', 'name', 'groupId'];
  return (
    isObject(value) &&
    sProps.every(prop => isString(value[prop])) &&
    USER_ROLES.some(v => v === value['role']) &&
    USER_LANGS.some(v => v === value['lang']) &&
    USER_TIMEZONES.some(v => v === value['tz'])
  );
}

export function isUserUpdateParams(value: unknown): value is UserUpdateParams {
  const sProps: Prop<UserUpdateParams, string>[] = [
    'name',
    'groupId',
    'serviceId',
    'supportTeam',
    'phoneNumber',
    'countryCode',
    'memo',
    'avatar',
  ];
  return (
    isObject(value) &&
    (sProps.some(prop => isString(value[prop])) ||
      USER_ROLES.some(v => v === value['role']) ||
      USER_LANGS.some(v => v === value['lang']) ||
      USER_TIMEZONES.some(v => v === value['tz']) ||
      isStringArray(value['callees']))
  );
}

export class Users extends ImmutableMap<string, User> {
  constructor(users?: User[] | Iterable<User>) {
    super(user => user.userId, users);
  }

  protected clone(users?: User[] | Iterable<User>): this {
    return new Users(users) as this;
  }
}
